import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: "pitchup", label: "Pitchup" },
  { id: "booking", label: "Booking.com" },
  { id: "airbnb", label: "Airbnb" },
  { id: "other", label: "Other" },
];

const CalendarSync = ({ units }) => {
  const [syncs, setSyncs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [form, setForm] = useState({
    unit_type: "lodge",
    unit_id: "",
    platform: "pitchup",
    ics_url: "",
  });
  const [exclusions, setExclusions] = useState([]);
  const [exBusy, setExBusy] = useState(false);
  const [exForm, setExForm] = useState({ unit_id: "", channel: "pitchup", start_date: "", end_date: "", note: "" });

  const currentType = useMemo(
    () => units.find((u) => u.type === form.unit_type),
    [units, form.unit_type]
  );

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/calendar-syncs`);
      setSyncs(data || []);
    } catch {
      toast.error("Failed to load calendar syncs.");
    }
    try {
      const { data } = await axios.get(`${API}/admin/channel-exclusions`);
      setExclusions(data || []);
    } catch {
      /* older backend — hide quietly */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addSync = async (e) => {
    e.preventDefault();
    if (!form.unit_id || !form.ics_url) {
      toast.error("Pick a unit and paste an ICS URL.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/calendar-syncs`, { ...form, enabled: true });
      toast.success("Calendar sync added. Running first sync…");
      const newId = `${form.unit_id}::${form.platform.toLowerCase()}`;
      setForm({ ...form, ics_url: "" });
      await load();
      // Kick off an immediate sync so the admin sees results
      try {
        await axios.post(`${API}/admin/calendar-syncs/${newId}/sync`);
      } catch {
        /* ignore — user can retry */
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add sync.");
    } finally {
      setLoading(false);
    }
  };

  const removeSync = async (id) => {
    if (!window.confirm("Remove this sync and clear its imported blocks?")) return;
    try {
      await axios.delete(`${API}/admin/calendar-syncs/${encodeURIComponent(id)}`);
      toast.success("Sync removed.");
      load();
    } catch {
      toast.error("Could not remove sync.");
    }
  };

  const syncOne = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await axios.post(
        `${API}/admin/calendar-syncs/${encodeURIComponent(id)}/sync`
      );
      if (data.status === "ok") {
        toast.success(`Synced — ${data.count ?? 0} event(s) imported.`);
      } else {
        toast.error(`Sync failed: ${data.error || "unknown"}`);
      }
      load();
    } catch {
      toast.error("Sync failed.");
    } finally {
      setSyncingId(null);
    }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await axios.post(`${API}/admin/calendar-syncs/sync-all`);
      const ok = (data.results || []).filter((r) => r.status === "ok").length;
      const total = (data.results || []).length;
      toast.success(`Synced ${ok}/${total} calendars.`);
      load();
    } catch {
      toast.error("Sync-all failed.");
    } finally {
      setSyncingAll(false);
    }
  };

  const copyExportUrl = (unitId) => {
    const url = `${PUBLIC_BASE}/api/ical/${unitId}.ics`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Export URL copied to clipboard."),
      () => toast.error("Could not copy — select and copy manually.")
    );
  };

  const channelFeedUrl = (unitId, channel) => `${PUBLIC_BASE}/api/ical/${unitId}.${channel}.ics`;

  const addExclusion = async (e) => {
    e.preventDefault();
    if (!exForm.unit_id || !exForm.start_date || !exForm.end_date) {
      toast.error("Pick a unit, channel and both dates.");
      return;
    }
    setExBusy(true);
    try {
      await axios.post(`${API}/admin/channel-exclusions`, exForm);
      toast.success("Dates hidden from that channel — the website stays bookable.");
      setExForm({ ...exForm, start_date: "", end_date: "", note: "" });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save.");
    } finally {
      setExBusy(false);
    }
  };

  const removeExclusion = async (id) => {
    if (!window.confirm("Remove this — the channel will see those dates as available again?")) return;
    try {
      await axios.delete(`${API}/admin/channel-exclusions/${id}`);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Could not remove.");
    }
  };

  const unitName = (id) => allUnits.find((u) => u.id === id)?.name || id;

  // Build a flat list of all units for the export URLs table
  const allUnits = useMemo(() => {
    const out = [];
    for (const t of units) {
      for (const u of t.units || []) {
        out.push({ ...u, unit_type: t.type, unit_type_name: t.name });
      }
    }
    return out;
  }, [units]);

  return (
    <section data-testid="calendar-sync-panel">
      <p className="overline text-[#6A5F58]">
        <span className="marker-line" /> Calendar sync
      </p>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">
        Two-way iCal sync
      </h2>
      <p className="mt-3 text-[#6A5F58] text-sm max-w-3xl">
        Prevent double bookings. Paste the ICS URLs Pitchup / Booking.com give you
        below — we fetch them every 15 minutes and block those dates automatically.
        Then paste our export URLs into their extranet so bookings taken here
        block them there too.
      </p>

      {/* IMPORT — external calendars we subscribe to */}
      <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-serif text-xl text-[#14100F]">Imports (external → us)</h3>
            <p className="text-xs text-[#6A5F58] mt-1">
              Booked on Pitchup / Booking.com → blocked here automatically.
            </p>
          </div>
          <button
            onClick={syncAll}
            disabled={syncingAll || syncs.length === 0}
            data-testid="calendar-sync-all"
            className="btn-secondary font-brand tracking-[0.14em] text-xs disabled:opacity-50"
          >
            {syncingAll ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Syncing all…
              </>
            ) : (
              <>
                <RefreshCw size={14} /> Sync all now
              </>
            )}
          </button>
        </div>

        <form onSubmit={addSync} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Unit type
            </Label>
            <Select
              value={form.unit_type}
              onValueChange={(v) => setForm({ ...form, unit_type: v, unit_id: "" })}
            >
              <SelectTrigger
                className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
                data-testid="calsync-unit-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {units.map((u) => (
                  <SelectItem key={u.type} value={u.type}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Unit
            </Label>
            <Select
              value={form.unit_id}
              onValueChange={(v) => setForm({ ...form, unit_id: v })}
            >
              <SelectTrigger
                className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
                data-testid="calsync-unit-id"
              >
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {(currentType?.units || []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Platform
            </Label>
            <Select
              value={form.platform}
              onValueChange={(v) => setForm({ ...form, platform: v })}
            >
              <SelectTrigger
                className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
                data-testid="calsync-platform"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
              Import URL (from that platform)
            </Label>
            <Input
              value={form.ics_url}
              onChange={(e) => setForm({ ...form, ics_url: e.target.value })}
              className="mt-2 bg-[#F5EFE6] h-11 rounded-lg"
              placeholder="https://…/calendar.ics"
              data-testid="calsync-url"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary font-brand tracking-[0.14em] w-full justify-center disabled:opacity-50"
              data-testid="calsync-submit"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            </button>
          </div>
        </form>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="calsync-table">
            <thead className="bg-[#F5EFE6] text-[#6A5F58] uppercase tracking-[0.18em] text-xs">
              <tr>
                <th className="text-left p-3">Unit</th>
                <th className="text-left p-3">Platform</th>
                <th className="text-left p-3">Import URL</th>
                <th className="text-left p-3">Last sync</th>
                <th className="text-left p-3">Events</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {syncs.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-[#6A5F58]">
                    No external calendars connected yet.
                  </td>
                </tr>
              )}
              {syncs.map((s) => (
                <tr key={s.id} className="border-t border-[#DDD1BE]">
                  <td className="p-3 text-[#14100F]">
                    <div className="font-medium">{s.unit_id}</div>
                    <div className="text-xs text-[#6A5F58]">{s.unit_type}</div>
                  </td>
                  <td className="p-3 text-[#14100F] capitalize">{s.platform}</td>
                  <td className="p-3 text-xs text-[#6A5F58] max-w-[280px]">
                    <div className="truncate" title={s.ics_url}>
                      {s.ics_url}
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {s.last_synced_at ? (
                      <div className="flex items-center gap-1.5">
                        {s.last_status === "ok" ? (
                          <CheckCircle2 size={13} className="text-[#3B8C4E]" />
                        ) : (
                          <AlertTriangle size={13} className="text-[#8C2E39]" />
                        )}
                        <span className="text-[#6A5F58]">
                          {new Date(s.last_synced_at).toLocaleString("en-GB")}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[#6A5F58]">Never</span>
                    )}
                    {s.last_error && (
                      <div className="text-[10px] text-[#8C2E39] mt-1 max-w-[220px] break-words">
                        {s.last_error}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-[#14100F]">{s.last_event_count ?? "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => syncOne(s.id)}
                      disabled={syncingId === s.id}
                      className="text-[#B76E79] hover:text-[#0F0E0E] p-2 rounded-full transition"
                      data-testid={`calsync-refresh-${s.id}`}
                      aria-label="Sync now"
                    >
                      {syncingId === s.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <RefreshCw size={16} />
                      )}
                    </button>
                    <button
                      onClick={() => removeSync(s.id)}
                      className="text-[#8C2E39] hover:text-[#5D1F27] p-2 rounded-full transition"
                      data-testid={`calsync-delete-${s.id}`}
                      aria-label="Remove sync"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CHANNEL-HIDDEN DATES — bookable here, closed on one channel */}
      <div className="mt-10 bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8">
        <h3 className="font-serif text-xl text-[#14100F]">Channel-hidden dates</h3>
        <p className="text-xs text-[#6A5F58] mt-1 max-w-3xl">
          Keep a date range bookable on walnutlakes.co.uk while one channel still sees it as busy — handy when
          you've moved a guest and don't want the channel re-selling the freed dates. Use the channel feed URL
          shown on the exclusion (paste it into that channel's extranet in place of the normal one).
        </p>

        <form onSubmit={addExclusion} className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Unit</Label>
            <Select value={exForm.unit_id} onValueChange={(v) => setExForm({ ...exForm, unit_id: v })}>
              <SelectTrigger className="mt-2 bg-[#F5EFE6] h-11 rounded-lg w-48" data-testid="excl-unit">
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {allUnits.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Hide from</Label>
            <Select value={exForm.channel} onValueChange={(v) => setExForm({ ...exForm, channel: v })}>
              <SelectTrigger className="mt-2 bg-[#F5EFE6] h-11 rounded-lg w-40" data-testid="excl-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">From</Label>
            <Input type="date" value={exForm.start_date} onChange={(e) => setExForm({ ...exForm, start_date: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg w-40" data-testid="excl-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-brand">To (inclusive)</Label>
            <Input type="date" value={exForm.end_date} onChange={(e) => setExForm({ ...exForm, end_date: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg w-40" data-testid="excl-to" />
          </div>
          <button
            type="submit"
            disabled={exBusy}
            className="btn-primary font-brand tracking-[0.14em] text-xs disabled:opacity-50 h-11"
            data-testid="excl-submit"
          >
            {exBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Hide dates
          </button>
        </form>

        {exclusions.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="excl-list">
            {exclusions.map((x) => (
              <div key={x.id} className="flex flex-wrap items-center gap-2 border border-[#EFE6D8] rounded-xl px-4 py-3" data-testid={`excl-row-${x.id}`}>
                <span className="text-sm font-medium text-[#14100F]">{unitName(x.unit_id)}</span>
                <span className="px-2 py-0.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] text-[10px] uppercase tracking-[0.12em] capitalize">{x.channel}</span>
                <span className="text-sm text-[#3A322E]">{x.start_date} → {x.end_date}</span>
                {x.note && <span className="text-xs text-[#6A5F58]">· {x.note}</span>}
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(channelFeedUrl(x.unit_id, x.channel)).then(
                      () => toast.success("Channel feed URL copied — paste it into the channel extranet."),
                      () => toast.error("Could not copy.")
                    );
                  }}
                  className="text-[#B76E79] hover:text-[#0F0E0E] p-2 rounded-full transition"
                  title="Copy this channel's feed URL"
                  data-testid={`excl-copy-${x.id}`}
                >
                  <Copy size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => removeExclusion(x.id)}
                  className="text-[#8C2E39] hover:text-[#5D1F27] p-2 rounded-full transition"
                  title="Remove"
                  data-testid={`excl-delete-${x.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* EXPORT — our feeds for the outside world */}
      <div className="mt-10 bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8">
        <h3 className="font-serif text-xl text-[#14100F]">Exports (us → external)</h3>
        <p className="text-xs text-[#6A5F58] mt-1">
          Copy these URLs and paste them into Pitchup / Booking.com so bookings taken
          on walnutlakes.co.uk block those platforms automatically.
        </p>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="calexport-table">
            <thead className="bg-[#F5EFE6] text-[#6A5F58] uppercase tracking-[0.18em] text-xs">
              <tr>
                <th className="text-left p-3">Unit</th>
                <th className="text-left p-3">Export URL</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {allUnits.map((u) => {
                const url = `${PUBLIC_BASE}/api/ical/${u.id}.ics`;
                return (
                  <tr key={u.id} className="border-t border-[#DDD1BE]">
                    <td className="p-3 text-[#14100F]">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-[#6A5F58]">{u.unit_type_name}</div>
                    </td>
                    <td className="p-3 text-xs text-[#6A5F58] max-w-[500px]">
                      <div className="truncate font-mono" title={url}>
                        {url}
                      </div>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => copyExportUrl(u.id)}
                        className="text-[#B76E79] hover:text-[#0F0E0E] p-2 rounded-full transition"
                        data-testid={`calexport-copy-${u.id}`}
                        aria-label="Copy export URL"
                      >
                        <Copy size={16} />
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-[#6A5F58] hover:text-[#0F0E0E] p-2 rounded-full transition"
                        data-testid={`calexport-open-${u.id}`}
                        aria-label="Open export URL"
                      >
                        <ExternalLink size={16} />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default CalendarSync;
